import * as Types from '../gql/_types.js';

import { ThreadModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = ThreadModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetThreadsQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.threads.errors = [];
    state.threads.loading = true;
    try {
        let { limit, offset, orderBy } = state.threads.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetThreads({...originalPayload, ...finalPayload})
        state.threads.meta.total = meta.aggregate?.count || 0;
        state.threads.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
    }
    state.threads.loading = false;
}

export const resetState: Action = ({state}) => {
    state.threads.errors = []
    state.threads.loading = false
    state.threads.items = {}
    state.threads.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.threads.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.threads.resetMeta()
    actions.threads.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.threads.errors = [];
    state.threads.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.threads.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetThreads({...originalPayload, ...finalPayload})
        state.threads.meta.total = meta.aggregate?.count || 0;
        state.threads.meta.page = newPage;
        state.threads.meta.offset = newOffset;
        state.threads.items = { ...state.threads.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
    }
    state.threads.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetThreadsQueryVariables> = async ({state, effects}, payload) => {
    state.threads.errors = [];
    state.threads.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetThreads(payload)
        state.threads.meta.total = meta.aggregate?.count || 0;
        state.threads.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
    }
    state.threads.loading = false;
}

export const getItem: AsyncAction<Types.GetThreadQueryVariables,Types.GetThreadQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetThread(payload);
        return item;
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetThreadQueryVariables> = async ({state, actions}, payload) => {
    state.threads.loading = true;
    try {
        let item = await actions.threads.getItem(payload);
        if (item) {
            state.threads.currentItemId = item.id;
            state.threads.items[item.id] = item;
        }
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
    }
    state.threads.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.threads.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.threads.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.ThreadFragment>> = async ({state}, meta) => {
    state.threads.meta = {
        ...state.threads.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateThreadMutationVariables['object'],Types.CreateThreadMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateThreadMutationVariables['object']>(payload);
    state.threads.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateThread({object:finalPayload});
        return item;
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateThreadMutationVariables,Types.UpdateThreadMutation['item']> = async ({state, effects}, payload) => {
    state.threads.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateThread(payload);
        return item;
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteThreadMutationVariables,Types.DeleteThreadMutation['item']> = async ({state, effects}, payload) => {
    state.threads.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteThread(payload);
        return item;
    } catch (e: any) {
        state.threads.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetThreadsMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetThreadsMetaSubscription, Types.SyncGetThreadsMetaSubscriptionVariables>(
        'syncGetThreadsMeta', 
        subscriptions.SyncGetThreadsMeta, 
        { where }, 
        actions.threads.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetThreadsMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetThreadsMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.threads.errors = [ ...state.threads.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.threads.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetThreadsSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.threads.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.threads.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetThreadsSubscription, Types.SyncGetThreadsSubscriptionVariables>(
        'syncGetThreads', 
        subscriptions.SyncGetThreads, 
        {...originalPayload, ...payload}, 
        actions.threads.onItemsData,
        () => { state.threads.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetThreads');
    state.threads.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetThreadsSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.threads.errors = [ ...state.threads.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.threads.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetThreadsSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.threads.syncGetItems(finalPayload);
    actions.threads.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.threads.unsubItemsData();
    actions.threads.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetThreadSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.threads.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetThreadSubscription, Types.SyncGetThreadSubscriptionVariables>(
        'syncGetThread', 
        subscriptions.SyncGetThread, 
        payload, 
        actions.threads.onItemData,
        () => { state.threads.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetThread');
    state.threads.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetThreadSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.threads.errors = [ ...state.threads.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.threads.currentItemId = payload.data.item.id;
        state.threads.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetThreadSubscriptionVariables> = async ({actions}, payload) => {
    actions.threads.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.threads.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};